import { ElementIdentifier as UiIdentifier, Page } from '@testsigma/organism';

import api from '../api/instance';
import { useInfiniteQuery, useQuery } from 'react-query';
import { NOOP } from '@testsigma/atoms';
import { PrivateURLConstants } from '@constants/private-url.constant';
import { dataurltoFile, isPreview } from '@utils/common';
import { getPreviewNameSpace } from '@modules/test-development/services/preview.service';
import { SavePoint } from '@modules/save-points/types/save-point';
import { getNextPageParam } from '@hooks/use-react-query-caching';
const nameSpace = PrivateURLConstants.ELEMENTS;
const importSpace = '/import';

const findAllPreviewMode = (query?, size?, page?, sort?, savePointId?) => {
  return api
    .get(`${getPreviewNameSpace(savePointId)}/elements/`, {
      query,
      size,
      page,
      sort,
    })
    .then((res) => new Page<UiIdentifier>().deserialize(res, UiIdentifier))
    .catch((e) => Promise.reject(e));
};
const findAll = (query?, size?, page?, sort?) => {
  return api
    .get(nameSpace, {
      query,
      size,
      page,
      sort,
    })
    .then((res) => new Page<UiIdentifier>().deserialize(res, UiIdentifier))
    .catch((e) => Promise.reject(e));
};

const filter = ({
  filterId,
  filterQuery = '',
}: {
  filterId: string;
  filterQuery?: string | Record<string, any>;
}): Promise<Page<UiIdentifier>> => {
  const isQueryString = !!(typeof filterQuery === 'string');
  return api
    .get(
      `${nameSpace}/filter/${filterId}${
        isQueryString && filterQuery ? filterQuery : ''
      }`,
      !isQueryString ? filterQuery : null,
    )
    .then((res) => new Page<UiIdentifier>().deserialize(res, UiIdentifier))
    .catch((e) => Promise.reject(e));
};

const show = (elementId: number, savePoint?: SavePoint) => {
  if (savePoint) {
    return api
      .get(`${getPreviewNameSpace(String(savePoint.id))}/elements/${elementId}`)
      .then((res) => new UiIdentifier().deserialize(res))
      .catch((e) => Promise.reject(e));
  }
  return api
    .get(nameSpace + '/' + elementId)
    .then((res) => new UiIdentifier().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const create = (element: UiIdentifier): Promise<UiIdentifier> => {
  return api
    .post(nameSpace, element)
    .then((res) => new UiIdentifier().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const bulkCreate = (uiIdentifiers: UiIdentifier[]) => {
  const request = [];
  uiIdentifiers.forEach((uiIdentifier) =>
    request.push(uiIdentifier.serialize()),
  );

  return api
    .post(nameSpace + '/bulk', request)
    .then((data: []) => {
      const uiIdentifiers: UiIdentifier[] = [];
      data.forEach((uiIdentifier) => {
        uiIdentifiers.push(new UiIdentifier().deserialize(uiIdentifier));
      });

      return uiIdentifiers;
    })
    .catch((e) => Promise.reject(e));
};

const update = (
  elementId: number,
  element: UiIdentifier,
  applicationVersionId: number | string,
) => {
  return api
    .put(`${nameSpace}/${elementId}`, element, {
      applicationVersionId: applicationVersionId,
    })
    .then((res) => new UiIdentifier().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const bulkDeleted = (applicationVersionId: number | string) => {
  return (ids: number[]) => {
    return api
      .remove(`${nameSpace}/bulk?${`ids[]`}=${ids.join()}`, {
        applicationVersionId: applicationVersionId,
      })
      .then((res) => res)
      .catch((e) => Promise.reject(e));
  };
};

const bulkSubmitReview = (
  ids: number[],
  applicationVersionId: number,
  params,
) => {
  return api
    .put(
      `${nameSpace}/bulk_update_status?${`ids[]`}=${ids.join()}&applicationVersionId=${applicationVersionId}`,
      {},
      params,
    )
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const bulkUpdate = (
  ids: number[],
  screenName: string,
  tags: string[],
  applicationVersionId: number | string,
) => {
  return api
    .put(
      `${nameSpace}/bulk_update?${encodeURIComponent(
        `ids[]`,
      )}=${ids.join()}&applicationVersionId=${applicationVersionId}`,
      tags,
      {
        screenName: screenName || '',
      },
    )
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const importData = (file: File, filterQuery: Record<string, any>) => {
  const formData: FormData = new FormData();
  formData.append('file', file);
  return api
    .put(
      `${nameSpace}${importSpace}`,
      formData,
      filterQuery,
      'multipart/form-data',
    )
    .then((res) => res)
    .catch((e) => {
      return Promise.reject(e);
    });
};

const importUFTData = (file: File, filterQuery: Record<string, any>) => {
  const formData: FormData = new FormData();
  formData.append('fileToImport', file);
  formData.append('isReplace', filterQuery.isReplace);
  formData.append('versionId', filterQuery.versionId);
  return api
    .post(`${nameSpace}/element_tsr_import`, formData, 'multipart/form-data')
    .then((res) => res)
    .catch((e) => {
      return Promise.reject(e);
    });
};

const getUFTImportStatus = (id: number) => {
  return api
    .get(`/settings/backups/${id}`)
    .then((res) => res)
    .catch((e) => {
      return Promise.reject(e);
    });
};

const getLastUFTImportStatus = (appVersionId: number) => {
  return api
    .get(
      `/settings/backups?query=applicationVersionId:${appVersionId},status:SUCCESS,backupType:UFT_ELEMENT_TSRX`,
    )
    .then((res) => res?.content?.[0] || null)
    .catch((e) => {
      return Promise.reject(e);
    });
};

const destroy = (id: number) => {
  return api
    .remove(`${nameSpace}/${id}`)
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const getObjectTypes = () => {
  return api
    .get(`${nameSpace}/object_types`)
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

export const useFetchAllElements = ({
  extraKey = null,
  query,
  size,
  page,
  sort,
  callback,
  enabled,
  savePointId = undefined,
  preQuery = NOOP,
}) => {
  const queryKey = ['fetch-all-elements', query, sort, page, extraKey];
  const isInPreviewMode = isPreview();
  return useQuery({
    queryKey,
    queryFn: () => {
      preQuery();
      if (isInPreviewMode) {
        return findAllPreviewMode(query, size, page, sort, savePointId);
      }
      return findAll(query, size, page, sort);
    },
    onSuccess: (data) => callback(true, data),
    onError: (err) => callback(false, err),
    enabled,
  });
};

const getElementImageUploadUrls = async ({ applicationVersionId, uuid }) =>
  api.get(
    `${nameSpace}/image_locator/upload_urls/${applicationVersionId}/${uuid}`,
  );

const uploadElementImage = async ({ presignedUploadUrl, base64_image }) => {
  const myHeaders = new Headers({ 'Content-Type': 'image/*' });
  await fetch(presignedUploadUrl, {
    method: 'PUT',
    headers: myHeaders,
    body: dataurltoFile(base64_image),
  });
};

const useInfiniteFetchAllElements = ({
  query,
  sort,
  size,
  cacheTime = 0,
  staleTime = 0,
}: {
  query: string;
  sort?: string;
  size?: number;
  cacheTime?: number;
  staleTime?: number;
}) => {
  const queryKey = 'elements-infinite-query';
  return useInfiniteQuery({
    queryKey: `${queryKey}-${query}`,
    queryFn: async ({ pageParam = 0 }) => {
      const response = await findAll(query, size, pageParam, sort);
      return response;
    },
    getNextPageParam,
    cacheTime,
    staleTime,
  });
};

const ElementService = {
  findAll,
  useInfiniteFetchAllElements,
  show,
  create,
  bulkCreate,
  update,
  filter,
  bulkDeleted,
  importData,
  destroy,
  bulkSubmitReview,
  bulkUpdate,
  importUFTData,
  getUFTImportStatus,
  getLastUFTImportStatus,
  getObjectTypes,
  getElementImageUploadUrls,
  uploadElementImage,
};

export default ElementService;
